import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  ACTOR_ID_HEADER_KEY,
  AUTHORIZATION_HEADER_KEY,
  IDENTITY_SERVICE_BASE_URL,
} from "constants";
import { useAuth } from "contexts/AuthContext";
import apiClient from "utils/apiClient";

const API_BASE_URL = `${IDENTITY_SERVICE_BASE_URL}/api/v1/applications`;

const unwrapApplications = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.payload)) return payload.payload;
  return [];
};

export default function useApplications() {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getRequestHeaders = useCallback(() => {
    const token = sessionStorage.getItem("authToken");
    return {
      [ACTOR_ID_HEADER_KEY]: user?.id || "",
      [AUTHORIZATION_HEADER_KEY]: token ? `Bearer ${token}` : "",
    };
  }, [user?.id]);

  // error handling
  const handleAxiosErrors = useCallback((err, action = "operation") => {
    const message =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      `Failed to ${action}`;

    setError(message);
    toast.error(message);
    console.error(`${action} error:`, err);
  }, []);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(API_BASE_URL, {
        headers: getRequestHeaders(),
      });
      const list = unwrapApplications(res.data);
      setApplications(list);
      return list;
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch applications";
      setError(message);
      toast.error(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getRequestHeaders]);

  // create
  const createApplication = useCallback(
    async (payload) => {
      setLoading(true);
      try {
        const res = await apiClient.post(API_BASE_URL, payload, {
          headers: getRequestHeaders(),
        });
        const newApp = res.data;
        await fetchApplications();
        toast.success("Application created successfully");
        return newApp;
      } catch (err) {
        handleAxiosErrors(err, "create application");
      } finally {
        setLoading(false);
      }
    },
    [getRequestHeaders, handleAxiosErrors],
  );

  // update
  const updateApplication = useCallback(
    async (id, payload) => {
      setLoading(true);
      try {
        const res = await apiClient.put(`${API_BASE_URL}/${id}`, payload, {
          headers: getRequestHeaders(),
        });
        const updatedApp = res.data;
        setApplications((prev) =>
          prev.map((app) => (app.id === id ? updatedApp : app)),
        );
        toast.success("Application updated successfully");
        return updatedApp;
      } catch (err) {
        handleAxiosErrors(err, "update application");
      } finally {
        setLoading(false);
      }
    },
    [getRequestHeaders, handleAxiosErrors],
  );

  // delete
  const deleteApplication = useCallback(
    async (id) => {
      setLoading(true);
      try {
        await apiClient.delete(`${API_BASE_URL}/${id}`, {
          headers: getRequestHeaders(),
        });
        setApplications((prev) => prev.filter((app) => app.id !== id));
        toast.success("Application deleted successfully");
        return true;
      } catch (err) {
        handleAxiosErrors(err, "delete application");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [getRequestHeaders, handleAxiosErrors],
  );

  // generate primary secret
  const generatePrimarySecret = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const res = await apiClient.post(
          `${API_BASE_URL}/${id}/secrets/primary`,
          null,
          { headers: getRequestHeaders() },
        );
        toast.success("Primary secret generated");
        // Refresh applications to reflect hasPrimarySecret
        await fetchApplications();
        return res.data.clientSecret;
      } catch (err) {
        handleAxiosErrors(err, "generate primary secret");
      } finally {
        setLoading(false);
      }
    },
    [getRequestHeaders, fetchApplications, handleAxiosErrors],
  );

  // rotate secret (returns new primary plaintext once)
  const rotateSecret = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const res = await apiClient.post(
          `${API_BASE_URL}/${id}/secrets/rotate`,
          null,
          { headers: getRequestHeaders() },
        );
        toast.success("Secret rotated successfully");
        await fetchApplications();
        return res.data.newClientSecret;
      } catch (err) {
        handleAxiosErrors(err, "rotate secret");
      } finally {
        setLoading(false);
      }
    },
    [getRequestHeaders, fetchApplications, handleAxiosErrors],
  );

  // revoke secondary secret
  const revokeSecondarySecret = useCallback(
    async (id) => {
      setLoading(true);
      try {
        await apiClient.delete(`${API_BASE_URL}/${id}/secrets/secondary`, {
          headers: getRequestHeaders(),
        });
        toast.success("Secondary secret revoked");
        await fetchApplications();
        return true;
      } catch (err) {
        handleAxiosErrors(err, "revoke secondary secret");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [getRequestHeaders, fetchApplications, handleAxiosErrors],
  );

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  return {
    applications,
    loading,
    error,
    refresh: fetchApplications,
    createApplication,
    updateApplication,
    deleteApplication,
    generatePrimarySecret,
    rotateSecret,
    revokeSecondarySecret,
  };
}
